/**
 * CT Chat Redux Slice
 * State management for Chat with CT - LLM-enabled conversational sales assistant
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  CTChatSession,
  CTChatMessage,
  CTChatCTA,
  CTChatChannel,
  CTChatSessionStatus,
  CTChatUserContext,
  CTChatRecommendation,
  CTChatSkill,
} from "@/models/ct-chat";
import { RootState } from "../store";

// ============================================================================
// State Interface
// ============================================================================

interface CTChatState {
  // Session state
  sessionId: string | null;
  channel: CTChatChannel;
  status: CTChatSessionStatus;

  // Messages
  messages: CTChatMessageUI[];
  isTyping: boolean;

  // User context built during qualification
  userContext: CTChatUserContext;

  // Recommendation state
  recommendation: CTChatRecommendationUI | null;

  // UI state
  isOpen: boolean;
  isMinimized: boolean;
  showStarters: boolean;

  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Page context
  pageUrl: string | null;
  referrer: string | null;
  campaign: string | null;
}

// UI-specific message type (without Firestore Timestamp)
export interface CTChatMessageUI {
  messageId: string;
  role: "user" | "assistant" | "system";
  content: string;
  skillUsed?: CTChatSkill;
  confidence?: number;
  ctas?: CTChatCTA[];
  timestamp: number; // Unix timestamp for UI
  isStreaming?: boolean;
}

// UI-specific recommendation type
export interface CTChatRecommendationUI {
  recommendedPass: string;
  confidence: number;
  rationale: string[];
  alternativePass?: string;
  shownAt: number;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: CTChatState = {
  sessionId: null,
  channel: "webchat",
  status: "active",
  messages: [],
  isTyping: false,
  userContext: {},
  recommendation: null,
  isOpen: false,
  isMinimized: false,
  showStarters: true,
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  isLoading: false,
  error: null,
  pageUrl: null,
  referrer: null,
  campaign: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Initialize a new CT Chat session
 */
export const initializeCTChatSession = createAsyncThunk(
  "ctChat/initializeSession",
  async (
    context: { pageUrl?: string; referrer?: string; campaign?: string },
    { rejectWithValue }
  ) => {
    try {
      // Generate a new session ID
      const sessionId = `ct_sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      return {
        sessionId,
        ...context,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to initialize session"
      );
    }
  }
);

/**
 * Send a message to the CT Chat
 */
export const sendCTChatMessage = createAsyncThunk(
  "ctChat/sendMessage",
  async (content: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { sessionId } = state.ctChatReducer;

      if (!sessionId) {
        return rejectWithValue("No active session");
      }

      const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      return {
        messageId,
        content,
        timestamp: Date.now(),
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to send message"
      );
    }
  }
);

/**
 * Handle CTA click
 */
export const handleCTAClick = createAsyncThunk(
  "ctChat/handleCTAClick",
  async (
    cta: CTChatCTA,
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const { sessionId } = state.ctChatReducer;

      if (!sessionId) {
        return rejectWithValue("No active session");
      }

      // Handle different CTA types
      switch (cta.type) {
        case "checkout":
          // Open checkout URL in new tab
          if (typeof window !== "undefined") {
            window.open(cta.action, "_blank");
          }
          break;

        case "compare":
          // Open comparison page
          if (typeof window !== "undefined") {
            window.open(cta.action, "_blank");
          }
          break;

        case "lead_capture":
          // Trigger lead capture flow - send message to bot
          dispatch(
            addUserMessage({
              content: "Email me a summary",
              timestamp: Date.now(),
            })
          );
          break;

        case "escalate":
          // Trigger escalation flow
          dispatch(
            addUserMessage({
              content: "I'd like to speak with someone",
              timestamp: Date.now(),
            })
          );
          break;
      }

      return { cta, sessionId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to handle CTA"
      );
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const ctChatSlice = createSlice({
  name: "ctChat",
  initialState,
  reducers: {
    // Session management
    setSessionId(state, action: PayloadAction<string>) {
      state.sessionId = action.payload;
    },

    setChannel(state, action: PayloadAction<CTChatChannel>) {
      state.channel = action.payload;
    },

    setStatus(state, action: PayloadAction<CTChatSessionStatus>) {
      state.status = action.payload;
    },

    // Message management
    addUserMessage(
      state,
      action: PayloadAction<{ content: string; timestamp: number }>
    ) {
      const { content, timestamp } = action.payload;
      state.messages.push({
        messageId: `msg_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
        role: "user",
        content,
        timestamp,
      });
      state.showStarters = false;
    },

    addAssistantMessage(state, action: PayloadAction<CTChatMessageUI>) {
      // Remove any existing streaming message
      state.messages = state.messages.filter((m) => !m.isStreaming);
      state.messages.push(action.payload);
      state.isTyping = false;
    },

    updateStreamingMessage(
      state,
      action: PayloadAction<{ content: string; isComplete?: boolean }>
    ) {
      const { content, isComplete } = action.payload;
      const streamingIndex = state.messages.findIndex((m) => m.isStreaming);

      if (streamingIndex !== -1) {
        state.messages[streamingIndex].content = content;
        if (isComplete) {
          state.messages[streamingIndex].isStreaming = false;
        }
      } else {
        // Create new streaming message
        state.messages.push({
          messageId: `msg_streaming_${Date.now()}`,
          role: "assistant",
          content,
          timestamp: Date.now(),
          isStreaming: !isComplete,
        });
      }
    },

    setTyping(state, action: PayloadAction<boolean>) {
      state.isTyping = action.payload;
    },

    clearMessages(state) {
      state.messages = [];
      state.showStarters = true;
    },

    // User context management
    updateUserContext(state, action: PayloadAction<Partial<CTChatUserContext>>) {
      state.userContext = { ...state.userContext, ...action.payload };
    },

    // Recommendation management
    setRecommendation(state, action: PayloadAction<CTChatRecommendationUI>) {
      state.recommendation = action.payload;
    },

    clearRecommendation(state) {
      state.recommendation = null;
    },

    // UI state management
    openChat(state) {
      state.isOpen = true;
      state.isMinimized = false;
    },

    closeChat(state) {
      state.isOpen = false;
    },

    minimizeChat(state) {
      state.isMinimized = true;
    },

    maximizeChat(state) {
      state.isMinimized = false;
    },

    toggleChat(state) {
      if (state.isOpen && !state.isMinimized) {
        state.isMinimized = true;
      } else if (state.isMinimized) {
        state.isMinimized = false;
      } else {
        state.isOpen = true;
      }
    },

    setShowStarters(state, action: PayloadAction<boolean>) {
      state.showStarters = action.payload;
    },

    // Connection state management
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
      state.isConnecting = false;
      if (action.payload) {
        state.connectionError = null;
      }
    },

    setConnecting(state, action: PayloadAction<boolean>) {
      state.isConnecting = action.payload;
    },

    setConnectionError(state, action: PayloadAction<string | null>) {
      state.connectionError = action.payload;
      state.isConnecting = false;
      state.isConnected = false;
    },

    // Page context
    setPageContext(
      state,
      action: PayloadAction<{
        pageUrl?: string;
        referrer?: string;
        campaign?: string;
      }>
    ) {
      const { pageUrl, referrer, campaign } = action.payload;
      if (pageUrl !== undefined) state.pageUrl = pageUrl;
      if (referrer !== undefined) state.referrer = referrer;
      if (campaign !== undefined) state.campaign = campaign;
    },

    // Error handling
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    // Reset state
    resetCTChat(state) {
      return { ...initialState, channel: state.channel };
    },
  },

  extraReducers: (builder) => {
    builder
      // Initialize session
      .addCase(initializeCTChatSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeCTChatSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionId = action.payload.sessionId;
        state.pageUrl = action.payload.pageUrl || null;
        state.referrer = action.payload.referrer || null;
        state.campaign = action.payload.campaign || null;
        state.status = "active";
      })
      .addCase(initializeCTChatSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Send message
      .addCase(sendCTChatMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendCTChatMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.messages.push({
          messageId: action.payload.messageId,
          role: "user",
          content: action.payload.content,
          timestamp: action.payload.timestamp,
        });
        state.showStarters = false;
        state.isTyping = true;
      })
      .addCase(sendCTChatMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Handle CTA click
      .addCase(handleCTAClick.fulfilled, (state) => {
        // CTA handled successfully
      })
      .addCase(handleCTAClick.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setSessionId,
  setChannel,
  setStatus,
  addUserMessage,
  addAssistantMessage,
  updateStreamingMessage,
  setTyping,
  clearMessages,
  updateUserContext,
  setRecommendation,
  clearRecommendation,
  openChat,
  closeChat,
  minimizeChat,
  maximizeChat,
  toggleChat,
  setShowStarters,
  setConnected,
  setConnecting,
  setConnectionError,
  setPageContext,
  setError,
  resetCTChat,
} = ctChatSlice.actions;

export default ctChatSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectCTChatSession = (state: RootState) => ({
  sessionId: state.ctChatReducer.sessionId,
  channel: state.ctChatReducer.channel,
  status: state.ctChatReducer.status,
});

export const selectCTChatMessages = (state: RootState) =>
  state.ctChatReducer.messages;

export const selectCTChatIsTyping = (state: RootState) =>
  state.ctChatReducer.isTyping;

export const selectCTChatIsOpen = (state: RootState) =>
  state.ctChatReducer.isOpen;

export const selectCTChatIsMinimized = (state: RootState) =>
  state.ctChatReducer.isMinimized;

export const selectCTChatShowStarters = (state: RootState) =>
  state.ctChatReducer.showStarters;

export const selectCTChatConnection = (state: RootState) => ({
  isConnected: state.ctChatReducer.isConnected,
  isConnecting: state.ctChatReducer.isConnecting,
  connectionError: state.ctChatReducer.connectionError,
});

export const selectCTChatRecommendation = (state: RootState) =>
  state.ctChatReducer.recommendation;

export const selectCTChatUserContext = (state: RootState) =>
  state.ctChatReducer.userContext;

export const selectCTChatError = (state: RootState) =>
  state.ctChatReducer.error;
